import Grid from '@mui/material/Grid';
import type { InventoryListByIdAPI } from '@openx/types/inventoryList.ts';
import {
  AppInventoryOption,
  AppInventoryVersion,
  type AppInventoryVersionType,
} from '@openx/types/targeting/appInventory.ts';
import {
  type InstanceProps,
  type InventoryAndContentState,
  InventoryContentOption,
  type ParentAccountFeatures,
} from '@openx/types/targeting/inventoryAndContent.ts';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes.ts';
import { noop } from '@openx/utils/noop.ts';
import { Fragment, type JSX, useMemo } from 'react';
import { getChipLabelVisibilityFlags } from '../../../utils/hooks/getChipLabelVisibilityFlags.ts';
import { useParameterRemove } from '../../../utils/hooks/useParameterRemove.ts';
import { useTargetingChildDisabledState } from '../../../utils/hooks/useTargetingChildDisabledState.ts';
import type { InvalidItemsError } from '../../shared/BulkAdd/types.ts';
import { DimensionChipLine } from '../../shared/DimensionChip/DimensionChipLine.tsx';
import { appInventoryFormPreviewSectionRenderer } from '../../shared/InventoryAndContent/components/appInventoryFormPreviewSectionRenderer.tsx';
import { useAppInventoryAllowed } from '../../shared/InventoryAndContent/featureFlags.ts';
import {
  TargetingDropdownSection,
  type TargetingDropdownSectionProps,
} from '../../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection.tsx';
import type { TargetingItemsProps } from '../../types.ts';
import type { SetInventoryListItemsDispatch } from '../InventoryAndContentTopBox/constants.ts';
import { useInventoryContentOptions } from '../InventoryAndContentTopBox/hooks/useInventoryContentOptions.ts';
import { getInventoryAndContentOptions } from './getInventoryAndContentOptions.tsx';
import { getInventoriesWithSettings } from './utils.ts';

interface InventoryAndContentFormPreviewItemsProps extends TargetingItemsProps<InventoryAndContentState> {
  inputInstanceValue?: InstanceProps | null;
  isBefInstance: boolean;
  appBundleIdDuplicateErrors: InvalidItemsError | null;
  setInventoryListItems?: SetInventoryListItemsDispatch;
  inventoryListItems?: InventoryListByIdAPI['values'];
  parentAccountFeatures: ParentAccountFeatures;
  inventoryVersion?: AppInventoryVersionType;
  optionsCount?: number;
}

export const InventoryAndContentFormPreviewItems = ({
  targetingParams,
  dispatchInventory = noop,
  readonly,
  instanceState,
  inputInstanceValue = null,
  isBefInstance,
  accountUid = '',
  appBundleIdDuplicateErrors,
  setInventoryListItems = noop,
  inventoryListItems = [],
  onOpenDrawerClick = noop,
  onRemove = noop,
  onRemoveParameter,
  parentAccountFeatures = { isAdUnitTargetingEnabled: false, isSiteTargetingEnabled: false },
  inventoryVersion,
  field,
}: InventoryAndContentFormPreviewItemsProps &
  Omit<TargetingDropdownSectionProps, 'isDisabled' | 'disabledReasonMessage'> & {
    onRemoveParameter?: (option: InventoryContentOption) => void;
  }) => {
  const { getDisabledStateForOption } = useTargetingChildDisabledState(field);

  const isAppInventoryAllowed = useAppInventoryAllowed();
  const inventoriesWithSettings = getInventoriesWithSettings(targetingParams);
  const { getOptionLabel, options } = useInventoryContentOptions({
    isBulk: false,
    parentAccountFeatures,
  });

  const switchState = targetingParams[InventoryContentOption.CONTENT_INTER_DIMENSION_OPERATOR] === Criteria.ANY;

  const hasAppInventoryData =
    isAppInventoryAllowed &&
    (inventoriesWithSettings.includes(InventoryContentOption.APP_GROUP) ||
      inventoriesWithSettings.includes(InventoryContentOption.APP_BUNDLE_ID));

  const appBundleIdState = getDisabledStateForOption(InventoryContentOption.APP_BUNDLE_ID);
  const appGroupState = getDisabledStateForOption(InventoryContentOption.APP_GROUP);

  const isCombinedAppInventorySectionDisabled = appBundleIdState.isDisabled || appGroupState.isDisabled;

  const combinedAppInventorySectionDisabledReasonMessage =
    appGroupState.disabledReasonMessage ?? appBundleIdState.disabledReasonMessage;

  const inventoryAndContentBase = getInventoryAndContentOptions({
    dispatchInventory,
    readonly,
    targetingParams,
    accountUid,
    appBundleIdDuplicateErrors,
    setInventoryListItems,
    inventoryListItems,
    switchState,
    instanceState,
    inputInstanceValue,
    isBefInstance,
    getDisabledReasonMessage: option => getDisabledStateForOption(option).errorReasonMessage,
  });

  const inventoryAndContent =
    inventoryVersion === AppInventoryVersion.APP_INVENTORY_EXCLUDED_V1
      ? Object.fromEntries(
          Object.entries(inventoryAndContentBase).filter(
            ([key]) => key !== AppInventoryOption.APP_BUNDLE_ID && key !== AppInventoryOption.APP_GROUP
          )
        )
      : inventoryAndContentBase;

  const parameterRemoveHandler = useParameterRemove<InventoryContentOption>(onRemoveParameter, onRemove);

  const availableOptions = useMemo(
    () =>
      options.reduce(
        (acc, key) => {
          if (key in inventoryAndContent) acc.push([key, inventoryAndContent[key]]);

          return acc;
        },
        [] as Array<[string, (isNotLastItem: boolean, showDimensionChipLine: boolean) => JSX.Element | null]>
      ),
    [options, inventoryAndContent]
  );

  const configuredKeySet = useMemo(() => new Set(inventoriesWithSettings), [inventoriesWithSettings]);

  const labelVisibilityFlags = useMemo(
    () =>
      getChipLabelVisibilityFlags(
        availableOptions.map(([key]) => key),
        key => configuredKeySet.has(key)
      ),
    [availableOptions, configuredKeySet]
  );

  return (
    <Grid>
      {availableOptions.map(([key, component], index) => {
        const isLastItem = index === availableOptions.length - 1;
        const showChipLabel = labelVisibilityFlags[index] ?? false;

        const isAppGroupWithCombinedSection = key === InventoryContentOption.APP_GROUP && hasAppInventoryData;
        const isSectionEmpty = isAppGroupWithCombinedSection
          ? !configuredKeySet.has(InventoryContentOption.APP_GROUP) &&
            !configuredKeySet.has(InventoryContentOption.APP_BUNDLE_ID)
          : !configuredKeySet.has(key);

        const { isDisabled: optionSectionDisabled, disabledReasonMessage: optionSectionDisabledReasonMessage } =
          getDisabledStateForOption(key);

        const isSectionDisabled = isAppGroupWithCombinedSection
          ? isCombinedAppInventorySectionDisabled
          : optionSectionDisabled;

        const sectionDisabledReasonMessage = isAppGroupWithCombinedSection
          ? combinedAppInventorySectionDisabledReasonMessage
          : optionSectionDisabledReasonMessage;

        return (
          <Fragment key={key}>
            <TargetingDropdownSection
              name={getOptionLabel(key)}
              isEmpty={isSectionEmpty}
              onOpenDrawerClick={() => onOpenDrawerClick(key)}
              onRemove={parameterRemoveHandler(key as InventoryContentOption)}
              isDisabled={isSectionDisabled}
              disabledReasonMessage={sectionDisabledReasonMessage}
              hasOptionsSelected={!isSectionEmpty}
            >
              {isAppGroupWithCombinedSection
                ? appInventoryFormPreviewSectionRenderer({
                    field,
                    targetingParams,
                    dispatchInventory,
                    switchState,
                    accountUid,
                    inventoryListItems,
                    setInventoryListItems,
                    inventoryVersion: undefined,
                    isDisabled: isSectionDisabled,
                    onOpenDrawerClick,
                    onRemoveAppName: noop,
                    onRemoveBundleIdManual: noop,
                    onRemoveBundleIdInventoryList: noop,
                  })
                : component(!isLastItem, false)}
            </TargetingDropdownSection>

            {!isLastItem && <DimensionChipLine switchState={switchState} showLabel={showChipLabel} />}
          </Fragment>
        );
      })}
    </Grid>
  );
};
